import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";
import {
  ultraadminApi,
  type ActAsPerson,
  type ActAsPartner,
  type ActAsSchool,
  type ActAsUltraadmin,
} from "@/services/api/ultraadmin";
import { setTokens } from "@/utils/tokenStorage";
import { extractErrorMessage } from "@/utils/errorHandler";
import type { AuthResponse } from "@/types";
import { ROLE_DASHBOARD } from "@/utils/talkWelt";
import { ROLE_LABELS, type Role } from "@/types/role";
import { LetterGlasDirectory } from "@/components/ui/LetterGlasDirectory";
import { TeacherLetterDirectory } from "@/components/teacher/TeacherLetterDirectory";

type Tab = "school" | "partner" | "ultraadmin";

const SCHOOL_ROLES: Role[] = [
  "teacher",
  "admin",
  "director",
  "hr",
  "superadmin",
  "engineer",
];

function applySession(res: AuthResponse) {
  setTokens(res.access_token, res.refresh_token);
  useAuthStore.setState({ user: res.user, isAuthenticated: true });
}

export function UltraadminActAsTeacherPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("school");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [schoolQ, setSchoolQ] = useState("");
  const [schoolSearch, setSchoolSearch] = useState("");
  const [schools, setSchools] = useState<ActAsSchool[]>([]);
  const [schoolId, setSchoolId] = useState("");
  const [personQ, setPersonQ] = useState("");
  const [personSearch, setPersonSearch] = useState("");
  const [people, setPeople] = useState<ActAsPerson[]>([]);
  const [picked, setPicked] = useState<ActAsPerson | null>(null);
  const [shellRole, setShellRole] = useState<Role | "">("");

  const [partnerQ, setPartnerQ] = useState("");
  const [partners, setPartners] = useState<ActAsPartner[]>([]);
  const [partnerId, setPartnerId] = useState("");
  const [partnerPeople, setPartnerPeople] = useState<ActAsPerson[]>([]);

  const [uaQ, setUaQ] = useState("");
  const [uas, setUas] = useState<ActAsUltraadmin[]>([]);

  useEffect(() => {
    const t = window.setTimeout(
      () => setSchoolSearch(schoolQ),
      schoolQ ? 300 : 0
    );
    return () => window.clearTimeout(t);
  }, [schoolQ]);
  useEffect(() => {
    const t = window.setTimeout(
      () => setPersonSearch(personQ),
      personQ ? 300 : 0
    );
    return () => window.clearTimeout(t);
  }, [personQ]);

  useEffect(() => {
    if (tab !== "school") return;
    let live = true;
    void ultraadminApi
      .listActAsSchools(schoolSearch || undefined)
      .then((res) => {
        if (live) setSchools(res.schools);
      })
      .catch((err) => {
        if (live)
          setError(
            extractErrorMessage(err, {
              loadError: "Schulen konnten nicht geladen werden",
            })
          );
      });
    return () => {
      live = false;
    };
  }, [tab, schoolSearch]);

  useEffect(() => {
    if (!schoolId) {
      setPeople([]);
      return;
    }
    let live = true;
    void ultraadminApi
      .listActAsPeople(schoolId, personSearch || undefined)
      .then((res) => {
        if (live) setPeople(res.people);
      })
      .catch((err) => {
        if (live)
          setError(
            extractErrorMessage(err, {
              loadError: "Personen konnten nicht geladen werden",
            })
          );
      });
    return () => {
      live = false;
    };
  }, [schoolId, personSearch]);

  useEffect(() => {
    if (tab !== "partner") return;
    let live = true;
    void ultraadminApi
      .listActAsPartners(partnerQ || undefined)
      .then((res) => {
        if (live) setPartners(res.partners);
      })
      .catch((err) => {
        if (live)
          setError(
            extractErrorMessage(err, {
              loadError: "Partner konnten nicht geladen werden",
            })
          );
      });
    return () => {
      live = false;
    };
  }, [tab, partnerQ]);

  useEffect(() => {
    if (!partnerId) {
      setPartnerPeople([]);
      return;
    }
    let live = true;
    void ultraadminApi
      .listActAsPartnerPeople(partnerId)
      .then((res) => {
        if (live) setPartnerPeople(res.people);
      })
      .catch((err) => {
        if (live)
          setError(
            extractErrorMessage(err, {
              loadError: "Personen konnten nicht geladen werden",
            })
          );
      });
    return () => {
      live = false;
    };
  }, [partnerId]);

  useEffect(() => {
    if (tab !== "ultraadmin") return;
    let live = true;
    void ultraadminApi
      .listActAsUltraadmins(uaQ || undefined)
      .then((res) => {
        if (live) setUas(res.ultraadmins);
      })
      .catch((err) => {
        if (live)
          setError(
            extractErrorMessage(err, {
              loadError: "Ultraadmins konnten nicht geladen werden",
            })
          );
      });
    return () => {
      live = false;
    };
  }, [tab, uaQ]);

  const go = async (res: AuthResponse, role: Role) => {
    applySession(res);
    navigate(ROLE_DASHBOARD[role] || "/dashboard", { replace: true });
  };

  const start = async (
    body: Parameters<typeof ultraadminApi.startActAs>[0],
    role: Role
  ) => {
    setBusy(true);
    setError(null);
    try {
      const res = await ultraadminApi.startActAs(body);
      await go(res, role);
    } catch (err) {
      setError(
        extractErrorMessage(err, {
          loadError: "Brille konnte nicht aufgesetzt werden",
        })
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-8">
      <h1 className="text-xl font-bold mb-2">Brille aufsetzen</h1>
      <p className="text-sm text-gray-500 mb-4">
        Person vollständig, oder Rolle ohne Person nur schauen. Login bleibt
        Ultraadmin.
      </p>
      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
      <div className="flex gap-2 mb-4">
        {(
          [
            ["school", "Schule"],
            ["partner", "Partner"],
            ["ultraadmin", "Ultraadmin"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={`px-3 py-1 text-sm rounded ${tab === id ? "bg-slate-900 text-white" : "bg-gray-100"}`}
            onClick={() => {
              setTab(id);
              setPicked(null);
              setShellRole("");
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "school" && (
        <>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Schule
          </label>
          <div className="mb-4">
            {schoolId ? (
              <div className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                <span className="min-w-0 truncate text-[13px] font-semibold">
                  {schools.find((s) => s.id === schoolId)?.name ?? "Schule"}
                </span>
                <button
                  type="button"
                  className="shrink-0 text-sm text-blue-700 underline"
                  onClick={() => {
                    setSchoolId("");
                    setPicked(null);
                    setShellRole("");
                    setPeople([]);
                  }}
                >
                  ändern
                </button>
              </div>
            ) : (
              <LetterGlasDirectory
                items={schools}
                getText={(s) => s.name}
                getKey={(s) => s.id}
                selectedKey={schoolId}
                query={schoolQ}
                onQueryChange={setSchoolQ}
                placeholder="Suchen…"
                empty="Keine Schule"
                ariaLabelPrefix="Schule"
                onSelect={(s) => {
                  if (!busy) {
                    setSchoolId(s.id);
                    setPicked(null);
                    setShellRole("");
                  }
                }}
                renderRow={(s) => (
                  <div className="min-w-0 truncate text-[13px] font-semibold">
                    {s.name}
                    {!s.is_active || s.is_deleted ? (
                      <span className="font-normal text-gray-500">
                        {" "}
                        · {s.is_deleted ? "gelöscht" : "inaktiv"}
                      </span>
                    ) : null}
                  </div>
                )}
              />
            )}
          </div>
          {schoolId && (
            <>
              <div className="mb-3 rounded-xl border border-gray-200 bg-white p-3.5">
                <TeacherLetterDirectory
                  title="Verzeichnis"
                  items={people.map((p) => ({
                    ...p,
                    id: p.teacher_id || p.user_id || p.email,
                  }))}
                  query={personQ}
                  onQueryChange={setPersonQ}
                  selectedId={
                    picked
                      ? picked.teacher_id || picked.user_id || picked.email
                      : null
                  }
                  onSelect={(p) => {
                    if (!busy) {
                      setPicked(p);
                      setShellRole("");
                    }
                  }}
                />
              </div>
              {picked && (
                <div className="mb-3">
                  <p className="text-sm font-medium mb-1">Startrolle</p>
                  <div className="flex flex-wrap gap-2">
                    {(picked.roles.length ? picked.roles : ["teacher"]).map(
                      (r) => (
                        <button
                          key={r}
                          type="button"
                          disabled={busy}
                          className="px-3 py-1 text-sm rounded bg-slate-900 text-white disabled:opacity-50"
                          onClick={() =>
                            void start(
                              {
                                role: r,
                                shell: false,
                                school_id: schoolId,
                                user_id: picked.user_id || null,
                                teacher_id: picked.teacher_id || null,
                              },
                              r as Role
                            )
                          }
                        >
                          {ROLE_LABELS[r as Role] || r}
                        </button>
                      )
                    )}
                  </div>
                </div>
              )}
              <p className="text-sm font-medium mb-1">
                Rolle ohne Person (nur schauen)
              </p>
              <div className="flex flex-wrap gap-2">
                {SCHOOL_ROLES.map((r) => (
                  <button
                    key={r}
                    type="button"
                    disabled={busy}
                    className={`px-3 py-1 text-sm rounded border ${shellRole === r ? "bg-amber-900 text-white" : "hover:bg-gray-50"}`}
                    onClick={() => {
                      setShellRole(r);
                      setPicked(null);
                      void start(
                        { role: r, shell: true, school_id: schoolId },
                        r
                      );
                    }}
                  >
                    {ROLE_LABELS[r]}
                  </button>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {tab === "partner" && (
        <>
          <div className="mb-4">
            <LetterGlasDirectory
              items={partners}
              getText={(p) => p.name}
              getKey={(p) => p.id}
              selectedKey={partnerId}
              query={partnerQ}
              onQueryChange={setPartnerQ}
              placeholder="Firma suchen…"
              empty="Keine Partner"
              ariaLabelPrefix="Partner"
              onSelect={(p) => {
                if (!busy) setPartnerId(p.id);
              }}
              renderRow={(p) => (
                <div className="min-w-0 truncate text-[13px] font-semibold">
                  {p.name}
                </div>
              )}
            />
          </div>
          {partnerId && (
            <>
              <div className="mb-3">
                <LetterGlasDirectory
                  items={partnerPeople}
                  getText={(p) => p.last_name}
                  getKey={(p) => p.user_id || p.email}
                  onSelect={(p) => {
                    if (busy) return;
                    void start(
                      {
                        role: "partner",
                        shell: false,
                        partner_company_id: partnerId,
                        user_id: p.user_id || null,
                      },
                      "partner"
                    );
                  }}
                  empty="Keine Person"
                  renderRow={(p) => (
                    <div className="min-w-0 truncate text-[13px] font-semibold">
                      {p.last_name}, {p.first_name}
                    </div>
                  )}
                />
              </div>
              <button
                type="button"
                disabled={busy}
                className="text-sm underline"
                onClick={() =>
                  void start(
                    {
                      role: "partner",
                      shell: true,
                      partner_company_id: partnerId,
                    },
                    "partner"
                  )
                }
              >
                Partner ohne Person (nur schauen)
              </button>
            </>
          )}
        </>
      )}

      {tab === "ultraadmin" && (
        <>
          <LetterGlasDirectory
            items={uas}
            getText={(u) => u.last_name}
            getKey={(u) => u.id}
            query={uaQ}
            onQueryChange={setUaQ}
            placeholder="Name oder E-Mail…"
            empty="Keine anderen Ultraadmins"
            onSelect={(u) => {
              if (!busy)
                void start(
                  { role: "ultraadmin", user_id: u.id, shell: false },
                  "ultraadmin"
                );
            }}
            renderRow={(u) => (
              <div className="min-w-0 truncate text-[13px] font-semibold">
                {u.last_name}, {u.first_name}
                <span className="font-normal text-gray-500">
                  {" "}
                  · nur schauen
                </span>
              </div>
            )}
          />
        </>
      )}
    </div>
  );
}
